import { bindable, resolve } from "aurelia";
import { IValidationRules, validationRulesRegistrar } from "@aurelia/validation";
import { paymentMessages } from "../../common/payment-messages";
import { addPaymentValidation } from "../../validation/payment-validation";
import { bankName } from "../../validation/account-number";

const SWEDISH_ONLY_METHODS = ["Bankkonto", "Bankgiro", "Plusgiro", "Personkonto"];

// Numbers each instance, so the ids its labels point to stay unique on the page.
let instances = 0;

export class PaymentFieldsCustomElement {
  validationRules = resolve(IValidationRules);
  uid = `payment-fields-${++instances}`;

  @bindable form;
  @bindable methods = [];
  @bindable lang = "sv";
  @bindable currency = null;
  @bindable format = "standard";
  @bindable showBankName = true;
  @bindable showClearingCode = true;
  @bindable showReference = false;
  @bindable referenceReadonly = false;
  @bindable showHolderName = false;
  @bindable normalize = false;
  @bindable referenceLabel;
  @bindable validateIbanWithBic = false;

  // Whether the current clearing number resolves to a known bank. When true the
  // Bank field is auto-filled and made read-only; when false it stays editable.
  bankKnown = false;
  _bankAutoFilled = false;

  get messages() {
    return paymentMessages[this.lang] || paymentMessages.sv;
  }

  get availableMethods() {
    if (!this.methods || this.methods.length === 0) return [];
    if (this.currency !== null && this.currency !== "SEK") {
      return this.methods.filter((m) => !SWEDISH_ONLY_METHODS.includes(m));
    }
    return this.methods;
  }

  getMethodLabel(method) {
    return this.messages.methodLabels[method] || method;
  }

  getAccountLabel() {
    const method = this.form?.paymentMethod;
    return this.messages.accountLabels[method] || "";
  }

  clearPaymentFields() {
    if (!this.form) return;
    this.form.paymentInformation = "";
    this.form.paymentBankCode = "";
    this.form.paymentDescription = "";
    this.bankKnown = false;
    this._bankAutoFilled = false;
    if (this.showHolderName) {
      this.form.paymentBankAccountHolderName = "";
    }
  }

  // Fills the Bank field from the clearing number (for Bankkonto). When the
  // clearing maps to a known bank the name is set and the field is read-only;
  // for an unknown clearing the field is left editable (and any value we
  // previously auto-filled is cleared so a stale name doesn't linger).
  // `clearing` is passed straight from the input event so the lookup uses the
  // freshest value regardless of binding timing.
  updateBankName(clearing) {
    if (!this.form) return;
    if (this.form.paymentMethod !== "Bankkonto" || !this.showBankName) return;
    const code = clearing != null ? clearing : this.form.paymentBankCode;
    const name = bankName(code);
    this.bankKnown = !!name;
    if (name) {
      this.form.paymentDescription = name;
      this._bankAutoFilled = true;
    } else if (this._bankAutoFilled) {
      this.form.paymentDescription = "";
      this._bankAutoFilled = false;
    }
  }

  normalizePaymentInformation() {
    if (!this.normalize) return;
    if (this.form?.paymentMethod === "IBAN" && this.form.paymentInformation) {
      this.form.paymentInformation = this.form.paymentInformation
        .replace(/\s+/g, "")
        .toUpperCase();
    }
  }

  normalizeBIC() {
    if (!this.normalize) return;
    if (this.form?.paymentBankCode) {
      this.form.paymentBankCode = this.form.paymentBankCode
        .replace(/\s+/g, "")
        .toUpperCase();
    }
  }

  attached() {
    this._applyValidation();
    this.updateBankName(); // reflect a pre-filled clearing when editing
  }

  formChanged(newForm, oldForm) {
    if (oldForm) {
      this._removePaymentRules(oldForm);
    }
    if (newForm && this._isAttached) {
      this._applyValidation();
    }
  }

  detaching() {
    this._isAttached = false;
    this._removePaymentRules(this.form);
  }

  _applyValidation() {
    if (!this.form) return;
    this._isAttached = true;

    const rulesBefore = this.validationRules.rules.length;
    addPaymentValidation(this.validationRules, {
      methods: this.methods,
      lang: this.lang,
      format: this.format,
      showBankName: this.showBankName,
      showClearingCode: this.showClearingCode,
      showHolderName: this.showHolderName,
      showReference: this.showReference,
      validateIbanWithBic: this.validateIbanWithBic,
    });

    // Collect the payment rules for later removal
    const paymentRules = this.validationRules.rules.slice(rulesBefore);
    this._paymentRules = new Set(paymentRules);

    validationRulesRegistrar.set(this.form, [...(validationRulesRegistrar.get(this.form) ?? []), ...paymentRules]);
  }

  _removePaymentRules(form) {
    if (!form || !this._paymentRules) return;
    const current = validationRulesRegistrar.get(form);
    if (!current) return;
    validationRulesRegistrar.set(form, current.filter((rule) => !this._paymentRules.has(rule)));
    this._paymentRules = null;
  }
}
